/**
 * The sign-in experience, implemented from the `Lumen Sign-in v3 Neon` design.
 *
 * WHAT WAS IMPLEMENTED AND WHAT WAS NOT, stated up front because the mockup has six
 * screens and this has four:
 *
 *   01 Sign in        implemented, wired to GoTrue
 *   02 Authenticating implemented — it is the real in-flight state, not a timed fake
 *   05 Reset password implemented, wired to GoTrue's recovery email
 *   06 Set password   implemented, and it is MANDATORY — see below
 *
 *   03 Two-factor     implemented against GoTrue's REAL MFA API (challenge/verify), and
 *                     shown only when the account has a VERIFIED TOTP factor. With none
 *                     enrolled the sign-in is already fully elevated and the step is
 *                     skipped — a code box that accepted any six digits would teach the
 *                     operator that 2FA protects them when it does not.
 *   04 Workspace      implemented, and appears only when the account can reach more than
 *                     one organisation. Today `profiles.organisation_id` gives exactly
 *                     one, so it stays hidden; the gate is the data, not a feature flag.
 *   07 Locked         implemented, driven by GoTrue's real 429 rate-limit response. A
 *                     client-side attempt counter would be reset by a reload, so it would
 *                     inconvenience honest users and stop nobody.
 *
 * The forced password change IS real and is the reason it is kept: a seeded or invited
 * credential is SHARED until it is replaced, and while it is shared the audit trail cannot
 * attribute an action to a person — which is the whole point of having one.
 *
 * ONE SCREEN HERE IS NOT IN THE MOCKUP AT ALL: the code entry (`CodeScreen`). Every
 * transactional template renders a six-digit `{{ .Token }}` under "Button not working? Sign
 * in with this code instead" and nothing in the app accepted it — no `verifyOtp` call
 * existed anywhere in the tree. The recipient the fallback exists for, whose mail gateway
 * rewrote the link, could not complete their invitation. The chip stays in the email, so
 * the screen has to exist.
 */
import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { BrandWordmark } from '@/components/lumen/BrandWordmark';
import { useAuth } from '@/lib/auth-context';
import { supabase, getRemember, setRemember } from '@/lib/supabase';
import { useNow } from '@/lib/use-now';
import { toast } from '@/app/toasts';
import { useTheme } from '@/app/theme';
import { Icon } from '@/components/lumen/Icon';
import { Toasts } from '@/components/lumen/Toasts';
import {
  absorbRateLimit,
  cooldownRemaining,
  emailResendKey,
  releaseCooldown,
  startCooldown,
} from '@/modules/auth/resend-cooldown';
import { PRODUCT, type SignInCopy, type SignInFeature } from '@/product/profile';
import { ParticleField } from './ParticleField';
import { CodeScreen, type CodePurpose } from './screens/CodeScreen';
import { LockedScreen } from './screens/LockedScreen';
import { MfaScreen } from './screens/MfaScreen';
import { SetPasswordScreen } from './screens/SetPasswordScreen';
import { notShipped } from '@/product/shipped';
import type * as SignInLookModule from '@/modules/appearance/SignInLook';
import './signin.css';

/* The company's saved look on the Cratus sign-in (Appearance, 0109). Lazy and Cratus-only, so
   Thunder's build never emits it; until it loads, the page's own particles and wordmark show. */
const SignInStage = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/appearance/SignInLook') : notShipped<typeof SignInLookModule>()).then((m) => ({ default: m.SignInStage })),
);
const SignInLogo = lazy(() =>
  (__PRODUCT__ === 'cratus' ? import('@/modules/appearance/SignInLook') : notShipped<typeof SignInLookModule>()).then((m) => ({ default: m.SignInLogo })),
);

type Screen = 'signin' | 'busy' | 'mfa' | 'forgot' | 'sent' | 'locked' | 'code';

/**
 * Why the sign-in frame is showing something other than the password form.
 *
 * Handed down by the auth gate, which is the only thing that can know: it reads the URL
 * this tab was opened with. See `modules/auth/auth-fragment.ts`.
 */
export interface SignInEntry {
  /**
   * Which code screen to open, or NULL when none of them applies.
   *
   * `parseAuthLink` recognises six link types and only three have a code screen. A failed
   * `signup`, `email_change` or `email` link used to fall through to a default of
   * `recovery`, so a broken email-change confirmation told the reader to "Enter your reset
   * code" — for a reset they never requested, from an email that contains no such code.
   * Null means: show the failure on the ordinary sign-in screen and stay there.
   */
  purpose: CodePurpose | null;
  /** The failure that sent the person here — an expired link, usually. */
  notice?: string | undefined;
  /**
   * The credential in the email is GONE, not merely unusable in link form.
   *
   * The distinction decides what the code screen may offer. A MALFORMED link — truncated by
   * a mail client, mangled by a scanner — leaves the six-digit code in the same email
   * perfectly valid, and offering it is the whole reason that screen exists. An EXPIRED or
   * already-spent link does not: GoTrue issues one credential per email and the code IS that
   * credential, so it died with the link. Asking someone to type it can only fail, and the
   * screen was doing exactly that with no other way forward on it.
   */
  expired?: boolean | undefined;
}

/* The call to action's glow — Thunder's amber, or the product's own (`--si-cta-*`, signin.css). */
const AMBER_GLOW = 'var(--si-cta-glow)';

/**
 * A white-label product's sign-in copy, or `null` for Thunder's own — which stays written into
 * this file below, exactly where it was, so a copy edit on `dev` merges without touching the
 * product seam. See src/product/profile.ts.
 */
const PRODUCT_COPY: SignInCopy | null = PRODUCT.signIn;

/** Thunder's legal and support links, which a product without its own keeps. */
const THUNDER_LEGAL: SignInCopy['legal'] = {
  terms: 'https://thunderenergy.ai/legal/terms',
  privacy: '/legal/privacy',
  support: 'mailto:operations@thunderenergy.ai?subject=Thunder%20sign-in%20help',
};

const LEGAL: SignInCopy['legal'] = PRODUCT_COPY?.legal ?? THUNDER_LEGAL;

/**
 * The forced password change, in the sign-in frame.
 *
 * Separate entry point rather than a screen inside `LumenSignIn`, because it is reached
 * with a VALID session — the auth gate routes to it after sign-in succeeds. Folding it
 * into the sign-in state machine would mean that machine had to know about an
 * authenticated state, which is how a "skip" path eventually gets added by accident.
 */
export function LumenSetPassword({
  onDone,
  reason = 'issued',
}: {
  /**
   * Fired once the password is actually changed.
   *
   * The gate reaches this screen by two routes — the `must_change_password` flag, which
   * clears itself, and an invite link's `type=invite`, which does not. Without this the
   * second route would hold the operator on the set-password screen after they had already
   * set one.
   */
  onDone?: (() => void) | undefined;
  /**
   * Which of the two routes it was.
   *
   * The screen's argument for itself — "this password was issued to you, so it must be
   * replaced" — is TRUE of an invite and FALSE of a reset, where nobody issued anything and
   * the operator asked to be here. Printing it anyway would be a small reassuring falsehood
   * in a product whose whole claim is that it does not print those.
   */
  reason?: 'issued' | 'reset' | undefined;
}): ReactNode {
  const { user, changePassword, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Frame>
      <SetPasswordScreen
        reason={reason}
        email={user?.email ?? 'unknown'}
        busy={busy}
        error={error}
        onSignOut={() => void signOut()}
        onSubmit={(next) => {
          setBusy(true);
          setError(null);
          void changePassword(next).then(({ error: err }) => {
            if (err) setError(err);
            else onDone?.();
            setBusy(false);
          });
        }}
      />
    </Frame>
  );
}

export function LumenSignIn({
  entry,
  onIntent,
}: {
  /** Present when this tab was opened by an invitation or reset link. */
  entry?: SignInEntry | undefined;
  /**
   * Tells the gate that the session about to arrive came from a link, so an invited user
   * reaches the set-password screen even if the backend's metadata write failed.
   */
  onIntent?: ((intent: 'invite' | 'recovery' | null) => void) | undefined;
} = {}): ReactNode {
  const { signIn } = useAuth();
  const [screen, setScreen] = useState<Screen>(entry?.purpose ? 'code' : 'signin');
  const [codePurpose, setCodePurpose] = useState<CodePurpose>(entry?.purpose ?? 'recovery');
  /* The link's failure, and only while it is the reason we are on the code screen — a
     "your invitation expired" banner over a password-reset code entry is a non sequitur. */
  const [codeNotice, setCodeNotice] = useState<string | null>(entry?.notice ?? null);

  /*
   * A FAILURE WITH NO CODE SCREEN STILL HAS TO BE SAID OUT LOUD.
   *
   * When `purpose` is null the reader lands on the ordinary sign-in form, and without this
   * the only sign their link failed is that nothing happened — the fragment is scrubbed on
   * capture, so there is not even a broken URL left to look at. The toast is the same
   * channel every other sign-in failure uses.
   */
  useEffect(() => {
    if (entry && entry.purpose === null && entry.notice) toast.error(entry.notice);
    /* Once, on mount: `entry` is a module-level capture and never changes. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [codeExpired, setCodeExpired] = useState<boolean>(entry?.expired ?? false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  /** Kept apart from `error`, which belongs to the password form. */
  const [sendError, setSendError] = useState<string | null>(null);

  async function onSignin(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setScreen('busy');

    const { error: err } = await signIn(email.trim(), password);

    if (err) {
      /*
       * GoTrue rate-limits by address and answers 429. Recognising it and showing the
       * lockout screen turns "Incorrect email or password" — which is not what happened
       * and sends the user to reset a password that was fine — into something actionable.
       */
      if (/too many|rate limit|429/i.test(err)) {
        const secs = /(\d+)\s*second/i.exec(err);
        setRetryAfter(secs?.[1] ? Number(secs[1]) : null);
        setScreen('locked');
        return;
      }
      setError(err);
      setScreen('signin');
      return;
    }

    /*
     * Password accepted. If the account carries a verified TOTP factor the session is
     * still AAL1 and must be elevated before it is useful, so the MFA step is entered
     * here rather than skipped.
     *
     * A failure listing factors is treated as "no factors": GoTrue is the authority on
     * whether elevation is required, and blocking a working sign-in on a metadata call
     * that failed would lock everyone out of a healthy system.
     */
    try {
      const { data } = await supabase.auth.mfa.listFactors();
      const totp = data?.totp?.find((f) => f.status === 'verified');
      if (totp) {
        setFactorId(totp.id);
        setScreen('mfa');
        return;
      }
    } catch {
      /* treated as no factors — see above */
    }

    /*
     * Otherwise nothing to do. A successful, fully-elevated sign-in changes the session
     * and the gate above this component swaps the whole tree, so this stays on `busy`
     * until it unmounts; setting a "done" state would race that unmount.
     */
  }

  /**
   * SEND THE RESET EMAIL — and stop claiming to have sent it when nothing was sent.
   *
   * ───────────────────────────────────────────────────────────────────────────
   * WHAT THIS USED TO DO, AND WHY IT WAS WRONG
   * ───────────────────────────────────────────────────────────────────────────
   *
   * `if (err) console.warn(...)` and then `setScreen('sent')` unconditionally. Pressing
   * "Send it again" inside GoTrue's 60-second `SMTP_MAX_FREQUENCY` window returns 429, and
   * the screen answered "Check your inbox" for an email the server had just refused to
   * send. So did a dead gateway and a broken mailer.
   *
   * The account-enumeration argument for showing "sent" either way is still right, and is
   * kept — but it only covers the UNKNOWN ADDRESS, which GoTrue answers 200 to anyway. It
   * never covered a transport failure: a 500 from the mailer and an unreachable stack
   * happen identically for an address that exists and one that does not, so surfacing them
   * leaks nothing and is the only way the operator learns to try again.
   *
   * A 429 is the one refusal that IS a signal — the frequency limit only bites for an
   * address GoTrue found — so it is absorbed into the cooldown and never shown.
   */
  async function sendReset(): Promise<boolean> {
    const address = email.trim();
    if (address.length === 0) return false;

    const key = emailResendKey(address);
    /* Belt to the disabled button's braces: a keyboard-driven resubmit reaches here. */
    if (cooldownRemaining(key) > 0) return false;

    setSendError(null);
    /* Started on the attempt, not the success — see resend-cooldown.ts for why the timer
       must look identical for an address that exists and one that does not. */
    startCooldown(key);

    const { error: err } = await supabase.auth.resetPasswordForEmail(address, {
      redirectTo: `${window.location.origin}/`,
    });

    if (!err) return true;

    if (absorbRateLimit(key, err.status, err.message)) {
      /* An email really had just been sent, so "check your inbox" is the truth. */
      return true;
    }

    const m = err.message.toLowerCase();
    if (
      m.includes('failed to fetch') || // Chrome
      m.includes('networkerror') || // Firefox
      m.includes('load failed') // Safari — its wording matches neither of the others, and
      // without this branch a Safari user with a dead stack was told the MAILER had failed
      // and then held for 60 seconds for a request that never left the browser.
    ) {
      /* Nothing left the browser, so nothing was sent and the wait is unearned. */
      releaseCooldown(key);
      setSendError(
        `Cannot reach the sign-in service. Nothing was sent — check your connection and try again.`,
      );
      return false;
    }

    /*
     * THE MAILER REFUSED, AND THE COOLDOWN MUST GO WITH IT.
     *
     * The cooldown starts on the ATTEMPT, so that the timer looks identical for an address
     * that exists and one that does not — which is the whole reason it is started early.
     * But a mailer failure (a 500 "Error sending recovery email", what a misconfigured relay
     * produces) left it running, and `onForgot`'s "already sent, show them the sent screen"
     * shortcut then fired on the SECOND press and rendered "Check your inbox. We sent a reset
     * link to …" for an email that was never sent. The one thing this screen must never do.
     *
     * Releasing it here costs the enumeration-timing property only in the case where the
     * mailer is broken for EVERY address, which tells an attacker nothing about any of them.
     */
    releaseCooldown(key);
    setSendError(
      'That email could not be sent. Try again in a moment, or ask your platform administrator to check email delivery.',
    );
    return false;
  }

  async function onForgot(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    /*
     * Already sent inside the window — they went back, came forward and pressed it again.
     * The email is in their inbox, so the screen that says so is the honest answer; before
     * this, `sendReset` refused and the form simply did nothing, which reads as broken.
     */
    const address = email.trim();
    if (address.length > 0 && cooldownRemaining(emailResendKey(address)) > 0) {
      setSendError(null);
      setScreen('sent');
      return;
    }

    if (await sendReset()) setScreen('sent');
  }

  return (
    <Frame>
      {screen === 'signin' && (
        <SignInScreen
          email={email}
          password={password}
          showPw={showPw}
          error={error}
          onEmail={setEmail}
          onPassword={setPassword}
          onToggle={() => setShowPw((v) => !v)}
          onSubmit={onSignin}
          onForgot={() => {
            setError(null);
            setScreen('forgot');
          }}
        />
      )}

      {screen === 'busy' && <BusyScreen />}

      {screen === 'mfa' && factorId && (
        <MfaScreen
          email={email}
          factorId={factorId}
          onRestart={() => {
            void supabase.auth.signOut();
            setPassword('');
            setFactorId(null);
            setScreen('signin');
          }}
        />
      )}

      {screen === 'locked' && (
        <LockedScreen
          email={email}
          retryAfterSeconds={retryAfter}
          onBack={() => {
            setRetryAfter(null);
            setScreen('signin');
          }}
        />
      )}

      {screen === 'forgot' && (
        <ForgotScreen
          email={email}
          error={sendError}
          onEmail={setEmail}
          onSubmit={onForgot}
          onBack={() => setScreen('signin')}
        />
      )}

      {screen === 'sent' && (
        <SentScreen
          email={email}
          error={sendError}
          onBack={() => setScreen('signin')}
          onResend={() => void sendReset()}
          onUseCode={() => {
            /* The reason the code exists: the link in the email did not survive the
               journey. Reachable from here, not only from an /accept-invite landing. */
            setCodePurpose('recovery');
            setCodeNotice(null);
            setScreen('code');
          }}
        />
      )}

      {screen === 'code' && (
        <CodeScreen
          purpose={codePurpose}
          email={email}
          onEmail={setEmail}
          notice={codeNotice}
          expired={codeExpired}
          /* The only self-service way back. An invitation has none — the form that sends it
             belongs to an administrator — so the screen says so instead of pretending. */
          onRequestNew={
            codePurpose === 'recovery'
              ? () => {
                  setCodeExpired(false);
                  setCodeNotice(null);
                  void sendReset();
                }
              : undefined
          }
          /* Registered BEFORE the verification lands — see the prop's own note. */
          onAttempt={() => onIntent?.(codePurpose === 'magiclink' ? null : codePurpose)}
          onRejected={() => onIntent?.(null)}
          onBack={() => setScreen('signin')}
        />
      )}
    </Frame>
  );
}

/* ══════════════════════════════════════════════════════════════════ frame ══ */

function Frame({ children }: { children: ReactNode }): ReactNode {
  /* `__PRODUCT__` is a build-time literal, so Thunder's bundle folds this away with the frame. */
  if (__PRODUCT__ === 'cratus') return <FescoFrame>{children}</FescoFrame>;
  return (
    <div
      className="lm-signin"
      /* Selects the product's sign-in palette in signin.css. Thunder's is the default block. */
      data-product={PRODUCT.id}
      style={{
        ['--font-heading' as string]: "'Inter', system-ui, sans-serif",
        ['--font-body' as string]: "'Inter', system-ui, sans-serif",
        /* Height lives in signin.css as a `100vh` -> `100dvh` pair with `overflow-y: auto`;
           React cannot hold two values for one property and the fallback needs exactly that.
           See the note there for why the frame is its own scroll container. */
        background: '#05070d',
        color: 'var(--si-ink, #fafafa)',
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/*
        * THE ROOM, IN TWO PLATES.
        *
        * Both photographs are used, cross-fading slowly rather than one being chosen. The
        * aisle is the literal subject — racks, containment, a corridor with a vanishing
        * point — and the field is the same room abstracted into light. Together they read as
        * one place seen two ways, which is the honest version of "datacenter" for a product
        * that monitors them.
        *
        * `aria-hidden`: they carry mood, not information. A screen reader announcing two
        * decorative photographs before the email field is noise.
        *
        * The fade is 24s with a 12s hold — slow enough that it is never the thing you are
        * looking at, which is the entire brief for a background behind a login form.
        */}
      <div className="lm-si-plate lm-si-plate-a" aria-hidden />
      <div className="lm-si-plate lm-si-plate-b" aria-hidden />

      {/*
        * THE SCRIM, and it is not optional.
        *
        * Measured on the raw aisle photograph, white body text over the lit racks falls to
        * roughly 2:1 — unreadable, and unreadable in a way that changes as the plates
        * cross-fade, so it cannot be fixed by choosing a text colour. The scrim is a
        * horizontal ramp, heaviest on the right where the card sits, so the corridor and its
        * vanishing point stay visible on the left where nothing has to be read.
        */}
      <div className="lm-si-scrim" aria-hidden />

      <ParticleField />

      {/* Above the plates and the stage, in the corner this screen leaves empty. */}
      <Toasts />

      <div className="lm-si-stage">
        {/*
          * The wordmark sits over the photograph rather than inside the card, so the brand
          * reads at full width and the card stays purely functional.
          */}
        <header className="lm-si-brandbar">
          {/*
            * 64, up from 42. The wordmark is the only brand element on a page whose right
            * half is a form, and at 42 it read as a favicon in the corner rather than as
            * the product's name. The height is a NUMBER rather than a CSS size because the
            * component derives its width from it to reserve the box before the artwork
            * decodes — sizing it in CSS would reintroduce the layout shift that guards
            * against.
            */}
          <BrandWordmark height={64} ink="light" />
        </header>

        <div className="lm-si-copy">
          <p className="lm-si-eyebrow">
            {PRODUCT_COPY?.eyebrow ?? <>Remote monitoring &amp; standards compliance</>}
          </p>
          <h1 className="lm-si-h1">
            <span className="lm-si-line" style={{ animationDelay: '80ms' }}>
              {PRODUCT_COPY?.headline[0] ?? 'Every watt'}
            </span>
            <span className="lm-si-line" style={{ animationDelay: '200ms' }}>
              {PRODUCT_COPY?.headline[1] ?? 'accounted for.'}
            </span>
          </h1>
          {/*
            * ONE SENTENCE BETWEEN THE HEADLINE AND THE ROTATOR.
            *
            * The headline is a claim and the rotator is a list of features; without
            * something between them the column jumps from slogan to specifics with nothing
            * saying what the product IS. This is the only static prose on the page and it
            * is the sentence a stranger reads to decide whether they are in the right
            * place.
            */}
          <p className="lm-si-lede">
            {PRODUCT_COPY?.lede ?? (
              <>
                Thunder models the power chain of a critical facility end to end — every source,
                path and load — and grades each site against the international standards that
                govern it.
              </>
            )}
          </p>
          <FeatureSlider features={PRODUCT_COPY?.features ?? FEATURES} />
          <ScopeStrip scope={PRODUCT_COPY?.scope ?? SCOPE} />
        </div>

        {/*
          * THE CARD FLOATS, on the right, over the photograph.
          *
          * Glass rather than a solid panel: the corridor stays faintly visible through it, so
          * the card reads as sitting IN the room rather than as a rectangle pasted over a
          * wallpaper. `backdrop-filter` is the whole effect, and it is applied to a fixed
          * element that never scrolls — blurring a scrolling surface is what makes this
          * technique expensive.
          */}
        <main className="lm-si-card">{children}</main>
      </div>
    </div>
  );
}

/**
 * CRATUS'S FRAME — the FESCO design's sign-in (§13): one rounded frame, an animated stage on the
 * left carrying the claim and a looping capability ticker, and a static panel on the right with
 * the form.
 *
 * ONLY THE FRAME IS NEW. Every screen inside it — sign-in, forgot, sent, code, MFA, locked,
 * set-password, workspace — is the same component Thunder renders, with the same state machine.
 * They read their colours from `--si-*` variables whose fallbacks are Thunder's literals, so
 * Thunder renders as it did and this frame states light and dark values for them (`signin.css`).
 *
 * UNLIKE THUNDER'S, THIS PAGE FOLLOWS THE THEME. The design's sign-in has a light/dark pill, and
 * light/dark is the person's choice (`theme.ts`), already known before `/me` — it is in their
 * browser. The particle field reads its colours once, at mount, so it is keyed on the theme.
 */
function FescoFrame({ children }: { children: ReactNode }): ReactNode {
  const theme = useTheme((t) => t.theme);
  const toggle = useTheme((t) => t.toggle);
  const ticker = (PRODUCT_COPY?.features ?? FEATURES).map((f) => f.module);
  const headline = PRODUCT_COPY?.headline ?? ['Every watt', 'accounted for.'];

  return (
    <div className="lm-signin lm-si-fesco" data-product={PRODUCT.id}>
      <Toasts />
      <div className="lm-sf-frame">
        <section className="lm-sf-left" aria-label={`${PRODUCT.name} platform`}>
          <div className="lm-sf-stage" aria-hidden>
            <Suspense fallback={<ParticleField key={theme} />}>
              <SignInStage mode={theme} />
            </Suspense>
          </div>
          <div className="lm-sf-left-top">
            <Suspense fallback={<BrandWordmark height={36} ink={theme === 'light' ? 'dark' : 'light'} />}>
              <SignInLogo mode={theme} height={36} />
            </Suspense>
          </div>
          <div className="lm-sf-hero">
            <span className="lm-sf-pill">{PRODUCT_COPY?.eyebrow ?? 'Remote monitoring'}</span>
            <h1>
              {headline[0]}
              <br />
              <span className="lm-sf-accent">{headline[1]}</span>
            </h1>
            {PRODUCT_COPY?.lede && <p className="lm-sf-lede">{PRODUCT_COPY.lede}</p>}
          </div>
          {/* Two identical sets so the loop is seamless; the second is hidden from readers. */}
          <div className="lm-sf-ticker" aria-label="Platform capabilities">
            <ul className="lm-sf-ticker-track">
              {[0, 1].map((set) => (
                <li key={set} className="lm-sf-ticker-set" aria-hidden={set === 1 ? true : undefined}>
                  <ul>
                    {ticker.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="lm-sf-right">
          <div className="lm-sf-top">
            <button
              type="button"
              className="lm-sf-theme"
              onClick={toggle}
              aria-label={theme === 'dark' ? 'Switch to the light theme' : 'Switch to the dark theme'}
              data-theme-now={theme}
            >
              <span className="lm-sf-sun">
                <Icon name="ic-sun" size={13} />
              </span>
              <span className="lm-sf-moon">
                <Icon name="ic-moon" size={13} />
              </span>
            </button>
            <a className="lm-sf-site" href="https://thunderenergy.ai" target="_blank" rel="noreferrer">
              <Icon name="ic-globe" size={14} />
              thunderenergy.ai
            </a>
          </div>
          <main className="lm-sf-form">{children}</main>
        </section>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════ screens ══ */

const RISE = 'lm-rise 340ms cubic-bezier(0.2,0.7,0.2,1) both';

function SignInScreen({
  email,
  password,
  showPw,
  error,
  onEmail,
  onPassword,
  onToggle,
  onSubmit,
  onForgot,
}: {
  email: string;
  password: string;
  showPw: boolean;
  error: string | null;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onToggle: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onForgot: () => void;
}): ReactNode {
  const [caps, setCaps] = useState(false);
  const [remember, setRememberState] = useState(() => getRemember());
  const disabled = email.trim().length === 0 || password.length === 0;
  useErrorToast(error);
  return (
    <section style={{ width: '100%', maxWidth: 400, animation: RISE }}>
      <h1 style={h1Style}>Welcome back.</h1>
      <p style={subStyle}>Use the email address your organisation invited.</p>

      <form onSubmit={onSubmit} style={{ display: 'grid', gap: 18, marginTop: 32 }}>
        <Field label="Email">
          <input
            className="lm-si-input"
            type="email"
            autoComplete="username"
            placeholder="you@operator.com"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            style={inputStyle}
            autoFocus
          />
        </Field>

        <Field label="Password">
          <span style={{ position: 'relative', display: 'flex' }}>
            <input
              className="lm-si-input"
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => onPassword(e.target.value)}
              /*
               * CAPS LOCK, read from the keyboard event rather than guessed.
               *
               * `getModifierState` is the only reliable source — there is no way to query
               * the lock from JavaScript, only to observe it during an event, which is why
               * this hangs off the field rather than living in component state somewhere.
               *
               * Worth the six lines: a password field masks its own contents, so caps lock
               * is the one typo a person cannot see themselves making, and the failure it
               * produces is indistinguishable from a wrong password.
               */
              onKeyUp={(e) => setCaps(e.getModifierState('CapsLock'))}
              onKeyDown={(e) => setCaps(e.getModifierState('CapsLock'))}
              onBlur={() => setCaps(false)}
              style={{ ...inputStyle, padding: '0 66px 0 13px' }}
            />
            <button
              type="button"
              onClick={onToggle}
              className="lm-si-pw"
              style={{
                position: 'absolute',
                right: 4,
                top: 4,
                bottom: 4,
                padding: '0 11px',
                border: 0,
                borderRadius: 7,
                background: 'none',
                font: 'inherit',
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--si-accent)',
                cursor: 'pointer',
              }}
            >
              {showPw ? 'Hide' : 'Show'}
            </button>
          </span>
        </Field>

        {caps && (
          <p className="lm-si-caps" role="status" style={{ margin: '7px 0 0', fontSize: 11.5, color: 'var(--si-warn, #fbbf24)' }}>
            Caps Lock is on.
          </p>
        )}


        {/*
          * BOTH OF THESE BELONG ABOVE THE BUTTON, and for the same reason: they are
          * decisions about the sign-in you are about to make, not things to consider after
          * you have made it. "Forgot your password?" under the button is a link you reach
          * only after failing; above it, it is an alternative you can take instead.
          */}
        <div className="lm-si-row">
          <label className="lm-si-remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => {
                setRememberState(e.target.checked);
                /*
                 * Written IMMEDIATELY, not on submit. The storage adapter reads this
                 * preference on every token write, and the write that matters happens
                 * inside signInWithPassword — so a value saved after the call would arrive
                 * one sign-in too late.
                 */
                setRemember(e.target.checked);
              }}
            />
            <span>Remember me</span>
          </label>

          <a
            href="#reset"
            className="lm-si-link"
            onClick={(e) => {
              e.preventDefault();
              onForgot();
            }}
          >
            Forgot your password?
          </a>
          {/*
            * The design shows "Two-factor required" here unconditionally. It is omitted
            * until the account actually has a factor — a permanent claim that 2FA is
            * required, on a deployment where it is not, is the kind of reassuring
            * falsehood a compliance product must not print.
            */}
        </div>

        <button type="submit" disabled={disabled} style={amberButton(disabled)} className="lm-si-amber">
          Sign in
        </button>
      </form>

      {/*
        * Legal and support, under the form.
        *
        * They are genuinely required — a product that processes an operator's identity owes
        * them a stated policy — and they are the one thing on this card that is not part of
        * signing in, so they sit outside the form and at the smallest legible size.
        *
        * Support is a `mailto:` rather than a route, because someone who cannot sign in
        * cannot reach a page behind the sign-in.
        */}
      <nav className="lm-si-legal" aria-label="Legal and support">
        <a className="lm-si-legal-link" href={LEGAL.terms} target="_blank" rel="noreferrer">Terms</a>
        <span aria-hidden>·</span>
        <a className="lm-si-legal-link" href={LEGAL.privacy}>Privacy Policy</a>
        <span aria-hidden>·</span>
        <a className="lm-si-legal-link" href={LEGAL.support}>
          Support
        </a>
      </nav>
      {/* A white-label product names the platform it runs on; Thunder is that platform. */}
      {PRODUCT_COPY?.poweredBy && <p className="lm-si-legal">{PRODUCT_COPY.poweredBy}</p>}

    </section>
  );
}

/**
 * The authenticating state.
 *
 * Shown for exactly as long as the request takes — it is not a timed animation. If the
 * network is fast this flashes, and that is correct: a deliberate minimum delay would be
 * inventing latency to look busy.
 */
function BusyScreen(): ReactNode {
  return (
    <section style={{ width: '100%', maxWidth: 400 }}>
      <svg width="42" height="42" viewBox="0 0 42 42" style={{ marginBottom: 26 }} aria-hidden="true">
        <circle cx="21" cy="21" r="17" stroke="#232936" strokeWidth="3" fill="none" />
        <circle
          cx="21"
          cy="21"
          r="17"
          stroke="#f59e0b"
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          strokeDasharray="28 80"
          style={{ transformOrigin: '21px 21px', animation: 'lm-spin 900ms linear infinite' }}
        />
      </svg>
      <h1 style={{ ...h1Style, fontSize: 'clamp(26px, 2.6vw, 35px)' }}>Signing you in.</h1>
      <p style={subStyle}>Checking your credentials and loading the sites you have access to.</p>
    </section>
  );
}

function ForgotScreen({
  email,
  error,
  onEmail,
  onSubmit,
  onBack,
}: {
  email: string;
  /** A transport or mailer failure. An unknown address never reaches here. */
  error: string | null;
  onEmail: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onBack: () => void;
}): ReactNode {
  const disabled = email.trim().length === 0;
  useErrorToast(error);
  return (
    <section style={{ width: '100%', maxWidth: 400, animation: RISE }}>
      <h1 style={h1Style}>Reset your password.</h1>
      <p style={subStyle}>
        Enter the address you sign in with. Your password does not change until you open
        the link we send.
      </p>
      <form onSubmit={onSubmit} style={{ display: 'grid', gap: 18, marginTop: 32 }}>
        <Field label="Email">
          <input
            className="lm-si-input"
            type="email"
            autoComplete="username"
            placeholder="you@operator.com"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            style={inputStyle}
            autoFocus
          />
        </Field>
        <button type="submit" disabled={disabled} style={amberButton(disabled)} className="lm-si-amber">
          Send reset link
        </button>
        <a
          href="#signin"
          className="lm-si-link"
          style={{ fontSize: 13 }}
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          Back to sign in
        </a>
      </form>
    </section>
  );
}

function SentScreen({
  email,
  error,
  onBack,
  onResend,
  onUseCode,
}: {
  email: string;
  error: string | null;
  onBack: () => void;
  onResend: () => void;
  onUseCode: () => void;
}): ReactNode {
  /*
   * A second hand, because this screen holds a countdown. `useNow` exists for exactly this
   * and is cheap; the alternative — an interval per screen — is what LockedScreen does and
   * is one clock too many for the same job.
   */
  const now = useNow(1000);
  const left = cooldownRemaining(emailResendKey(email), now);

  useErrorToast(error);
  return (
    <section style={{ width: '100%', maxWidth: 400, animation: RISE }}>
      <h1 style={h1Style}>Check your inbox.</h1>
      <p style={subStyle}>
        We sent a reset link to <span style={{ color: 'var(--si-ink-2, #d4d4d8)' }}>{email}</span>. If it
        does not arrive within a few minutes, check your spam folder, then send it again
        below.
      </p>


      <div style={{ display: 'flex', alignItems: 'center', gap: 22, marginTop: 30 }}>
        <button type="button" onClick={onBack} className="lm-si-ghost" style={ghostButton}>
          Back to sign in
        </button>
        {/*
          * The wait is SHOWN, not just enforced. GoTrue refuses a second mail inside 60
          * seconds and this button used to fire straight into that 429 — so the remaining
          * time is the one thing the reader needs, and a dead link with no explanation is
          * what they used to get instead.
          */}
        {left > 0 ? (
          <span
            className="lm-num"
            style={{ fontSize: 13, color: 'var(--si-ink-3, #71717a)', fontFeatureSettings: "'tnum' 1" }}
          >
            You can send it again in {left}s
          </span>
        ) : (
          <a
            href="#resend"
            className="lm-si-link"
            style={{ fontSize: 13 }}
            onClick={(e) => {
              e.preventDefault();
              onResend();
            }}
          >
            Send it again
          </a>
        )}
      </div>

      {/*
        * The code fallback, reachable. Every template prints a six-digit code under "Button
        * not working?" — this is the screen that finally accepts it, and a recipient whose
        * link was rewritten by their own mail gateway has no other way through.
        */}
      <p style={{ fontSize: 13, lineHeight: 1.65, margin: '28px 0 0', color: 'var(--si-ink-3, #71717a)' }}>
        Link broken or rewritten by your mail system?{' '}
        <a
          href="#code"
          className="lm-si-link"
          onClick={(e) => {
            e.preventDefault();
            onUseCode();
          }}
        >
          Enter the code from the email
        </a>
        .
      </p>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════ atoms ══ */

const h1Style = {
  fontWeight: 600,
  fontSize: 'clamp(30px, 3vw, 41px)',
  lineHeight: 1.1,
  letterSpacing: '-0.04em',
  margin: 0,
  color: 'var(--si-ink, #fafafa)',
} as const;

const subStyle = {
  fontSize: 15,
  lineHeight: 1.6,
  margin: '14px 0 0',
  color: 'var(--si-sub, #a1a1aa)',
} as const;

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box' as const,
  minHeight: 46,
  padding: '0 13px',
  font: 'inherit',
  fontSize: 15,
  color: 'var(--si-ink, #fafafa)',
  background: 'var(--si-field, #12161f)',
  border: '1px solid var(--si-line, #283041)',
  borderRadius: 10,
  outline: 'none',
};

const ghostButton = {
  minHeight: 46,
  paddingInline: 22,
  border: '1px solid var(--si-line, #2f3646)',
  borderRadius: 10,
  background: 'var(--si-field, #12161f)',
  font: 'inherit',
  fontSize: 14.5,
  fontWeight: 500,
  color: 'var(--si-ink, #e4e4e7)',
  cursor: 'pointer',
  whiteSpace: 'nowrap' as const,
  flex: 'none',
};

/** The call to action. Named for Thunder's amber; the colours are the product's `--si-cta-*`. */
function amberButton(disabled: boolean) {
  return {
    minHeight: 48,
    marginTop: 4,
    border: 0,
    borderRadius: 10,
    font: 'inherit',
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: '0.01em',
    color: 'var(--si-cta-ink)',
    background: 'linear-gradient(135deg, var(--si-cta-from), var(--si-cta-to))',
    boxShadow: disabled ? 'none' : AMBER_GLOW,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
  } as const;
}

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label style={{ display: 'grid', gap: 7 }}>
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.13em',
          textTransform: 'uppercase',
          color: 'var(--si-ink-3, #8b8b95)',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

/**
 * Raise the screen's error as a toast rather than a note inside the card.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT LEFT THE CARD
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * An error printed inside the sign-in card competes with the thing it is about. It pushes
 * the button down as it appears, so a second click can land somewhere else; it sits below
 * the fold on a short window; and on the "Check your inbox" screen it appeared under a
 * heading saying the mail had been sent, which reads as a contradiction rather than a
 * correction.
 *
 * A toast sits in the corner, over nothing, and says when it will go. The sign-in screen
 * has no chrome in its top right, which is why they go there and the alarm stack does not.
 *
 * The `error` prop is kept rather than removed: the screens still own their error state and
 * one day may want it inline again (a field-level message, for instance, belongs beside the
 * field). What changed is where it is drawn.
 */
function useErrorToast(error: string | null): void {
  useEffect(() => {
    if (error !== null && error !== '') toast.error(error);
  }, [error]);
}


/**
 * The feature rotator under the headline.
 *
 * I ARGUED AGAINST A ROTATOR ON THIS PAGE and the owner asked for one, so here it is — but
 * built so the objection does not apply. The original design was a four-slide carousel of
 * ARCHITECTURES: diagrams, tier targets, a different composition every few seconds beside
 * the password field. This rotates TEXT inside a box whose height is reserved for the
 * tallest slide. Nothing reflows, nothing moves in the periphery, and the form never shifts
 * under the cursor.
 *
 * It also stops when you are using the page. Focus anything, hover the copy, or switch tabs
 * and it holds — a sentence changing while you read it is the actual annoyance, not the
 * rotation itself.
 *
 * WHY FIVE, AND WHY THESE FIVE. The owner asked for the most important features rather than
 * the one-line claims that were here before. These are the five the product is actually
 * bought for, one per major surface, and each names a real module: two from Monitor, one
 * from Engineer, two from Comply. None of them describes anything that does not ship.
 */
const FEATURES: readonly SignInFeature[] = [
  {
    module: 'Monitor',
    k: 'Live plant telemetry',
    v: 'Mains, generator, UPS and distribution, read where the current actually flows — live from the plant, not from a nightly export.',
  },
  {
    module: 'Monitor',
    k: 'A twin of the real chain',
    v: 'A 2D isometric and a 3D model built from the wiring itself, so what you inspect is the plant as it is rather than as the inventory guessed.',
  },
  {
    module: 'Engineer',
    k: 'Single-line diagram studio',
    v: 'Draw the plant as it is wired. Every node carries its own measurement source, so the diagram and the readings cannot drift apart.',
  },
  {
    module: 'Comply',
    k: '38 standards, every clause cited',
    v: 'IEC, IEEE, NFPA and TIA evaluated against each site’s real parameters. A check that cannot name the clause it enforces does not ship.',
  },
  {
    module: 'Comply',
    k: 'Evidence-grade reports',
    v: 'The parameter, the clause, the verdict and the reading behind it — dated, and reproducible from the same inputs a year from now.',
  },
];

/**
 * The three numbers that size the product.
 *
 * Real counts, not round ones: 38 standards, 13 archetypes and 15 industries are what the
 * engine actually carries (see CLAUDE.md). A login page is the one place a prospective
 * operator reads the scope of the thing before they are inside it, and an invented figure
 * here is the kind that gets quoted back in a procurement meeting.
 */
const SCOPE: readonly { n: string; l: string }[] = [
  { n: '38', l: 'Standards' },
  { n: '13', l: 'Plant archetypes' },
  { n: '15', l: 'Industries' },
];

function FeatureSlider({ features }: { features: readonly SignInFeature[] }): ReactNode {
  const [i, setI] = useState(0);
  const [held, setHeld] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || held) return;
    /* 7.2s, up from 6.2: these slides carry a heading AND a sentence, and the old interval
       was tuned for a single line. A rotator that moves on before the copy is read is worse
       than no rotator. */
    const t = window.setInterval(() => setI((n) => (n + 1) % features.length), 7200);
    return () => window.clearInterval(t);
  }, [held, features.length]);

  useEffect(() => {
    const onVis = (): void => setHeld(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const f = features[i];
  if (!f) return null;

  return (
    <div
      className="lm-si-feat"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocusCapture={() => setHeld(true)}
      onBlurCapture={() => setHeld(false)}
    >
      {/*
        * `aria-live="off"`: this is decorative rotation, and announcing a new feature every
        * seven seconds over a password field is hostile to a screen reader. The text is
        * still in the accessibility tree and readable on demand.
        */}
      <div className="lm-si-feat-text" key={i} aria-live="off">
        {/* Mono, because it is a label rather than prose — the same typographic split the
            console uses for every identifier and count. */}
        <p className="lm-si-feat-tag">
          <span className="lm-si-feat-idx">{String(i + 1).padStart(2, '0')}</span>
          <span className="lm-si-feat-sep" aria-hidden>
            /
          </span>
          <span className="lm-si-feat-mod">{f.module}</span>
        </p>
        <h2 className="lm-si-feat-h">{f.k}</h2>
        <p className="lm-si-feat-p">{f.v}</p>
      </div>

      <div className="lm-si-ticks" role="tablist" aria-label="Features">
        {features.map((st, n) => (
          <button
            key={st.k}
            type="button"
            role="tab"
            aria-selected={n === i}
            aria-label={st.k}
            className={n === i ? 'lm-si-tick lm-si-tick-on' : 'lm-si-tick'}
            onClick={() => setI(n)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The scope strip that closes the column.
 *
 * Static on purpose. Everything above it moves; a page whose every element rotates has no
 * anchor, and these three numbers are the one thing on the screen worth reading twice.
 */
function ScopeStrip({ scope }: { scope: SignInCopy['scope'] }): ReactNode {
  /* A product with no real counts to print gets no strip, never a placeholder. */
  if (scope.length === 0) return null;
  return (
    <dl className="lm-si-scope">
      {scope.map((s) => (
        <div className="lm-si-scope-item" key={s.l}>
          <dt className="lm-si-scope-n">{s.n}</dt>
          <dd className="lm-si-scope-l">{s.l}</dd>
        </div>
      ))}
    </dl>
  );
}
